import { Suspense, useMemo } from "react";
import { baseUrl, fetchKmsData } from "@mediaspace/shared/utils";
import { ErrorBoundary } from "@mediaspace/shared/ui";
import { SidebarSkeleton } from "./sidebar-skeleton/SidebarSkeleton";
import { KmsTypeComponent } from "@mediaspace/shared/types";
import { KmsTypeHeroProps } from "@mediaspace/shared/types";
import { styled } from "@mui/material";
import { Box } from "@mediaspace/shared/styled";
import CtaContainer from "./cta-container/CtaContainer";

export interface SidebarContainerProps
    extends Pick<KmsTypeHeroProps, "categoryId" | "playlist"> {
    entryId: string;
    onEmptyData?: () => void;
}

/**
 * ts representation of Kms_Type_Media_Sidebar_Props
 */
type KmsTypeSidebarProps = {
    cta?: KmsTypeComponent[];
    sidebarTop?: KmsTypeComponent;
    sidebarLists?: KmsTypeComponent[];
};

interface SidebarProps {
    fetchData: () => KmsTypeSidebarProps;
    onEmptyData?: () => void;
}

/**
 * media page Sidebar component -
 * render the relevant sidebar conponents as received from kms
 */
function Sidebar(props: SidebarProps) {
    const { fetchData, onEmptyData } = props;
    const sidebarContent = fetchData();
    const { cta, sidebarTop, sidebarLists } = sidebarContent;

    const className = "kms-ds-media-page-sidebar";

    const renderComponent = (component: KmsTypeComponent) => {
        const { component: Component, props } = component;
        return (
            <ErrorBoundary fallback={<></>}>
                <Component {...props} />
            </ErrorBoundary>
        );
    };

    const noSidebarTopAndLists = !sidebarTop && sidebarLists?.length === 0;

    if (noSidebarTopAndLists) {
        onEmptyData && onEmptyData();
        return <></>;
    }

    return (
        <>
            {cta && cta.length > 0 && (
                <CtaContainer
                    components={cta}
                    renderComponent={renderComponent}
                    className={className + "-cta"}
                    noSidebarTopAndLists={noSidebarTopAndLists}
                />
            )}
            {sidebarTop && <StyledGapContainer className={className + "-top"}>{renderComponent(sidebarTop)}</StyledGapContainer>}
            {sidebarLists && sidebarLists[0] && <div className={className+"-list"}>{renderComponent(sidebarLists[0])}</div>}
        </>
    );
}

/**
 * media page Sidebar component container - load sidebar components from kms
 */
export function SidebarContainer(props: SidebarContainerProps) {
    const { entryId, categoryId, playlist, onEmptyData } = props;

    const url =
        baseUrl +
        "/default/media/sidebar/id/" +
        entryId +
        (categoryId ? "/categoryid/" + categoryId : "") +
        (playlist ? "/playlistid/" + playlist.id : "");

    const fetchData = useMemo(() => fetchKmsData<KmsTypeSidebarProps>(url), [url]);

    return (
        <ErrorBoundary fallback={<SidebarSkeleton />}>
            <Suspense fallback={<SidebarSkeleton />}>
                <Sidebar fetchData={fetchData} onEmptyData={onEmptyData} />
            </Suspense>
        </ErrorBoundary>
    );
}

// A box that adds a margin between sidebar components
const StyledGapContainer = styled(Box)(({theme}) => ({
    marginBottom: theme.spacing(4),
}));

export default SidebarContainer;
